/**
 * Reset Password Page
 *
 * Page for users to reset their password using a token from email
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { authService } from '../../services/auth';
import type { ResetPasswordData } from '../../types';

export default function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token') || '';
  const emailParam = searchParams.get('email') || '';

  const [email, setEmail] = useState(emailParam);
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validate token and email are present
  useEffect(() => {
    if (!token || !emailParam) {
      setError(t('reset_password.invalid_link'));
      toast.error(t('reset_password.invalid_link_toast'));
    }
  }, [token, emailParam, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!email || !password || !passwordConfirmation) {
      setError(t('reset_password.all_fields_required'));
      return;
    }

    if (password !== passwordConfirmation) {
      setError(t('reset_password.passwords_mismatch'));
      return;
    }

    if (password.length < 8) {
      setError(t('reset_password.password_too_short'));
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const data: ResetPasswordData = {
        token,
        email,
        password,
        password_confirmation: passwordConfirmation,
      };

      await authService.resetPassword(data);

      toast.success(t('reset_password.success_message'));
      navigate('/login');
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || t('reset_password.reset_failed');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gray-50">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="text-center text-3xl font-bold text-gray-900">{t('reset_password.title')}</h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {t('reset_password.description')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="label">
                {t('reset_password.email_label')}
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="label">
                {t('reset_password.password_label')}
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input"
                  placeholder={t('reset_password.password_placeholder')}
                  disabled={isSubmitting}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {t('reset_password.password_hint')}
              </p>
            </div>

            <div>
              <label htmlFor="password_confirmation" className="label">
                {t('reset_password.confirm_password_label')}
              </label>
              <div className="mt-1">
                <input
                  id="password_confirmation"
                  name="password_confirmation"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  className="input"
                  placeholder={t('reset_password.confirm_password_placeholder')}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting || !token || !emailParam}
                className="btn btn-primary w-full"
              >
                {isSubmitting ? t('reset_password.submitting') : t('reset_password.submit_button')}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">{t('reset_password.or')}</span>
              </div>
            </div>

            <div className="mt-6 text-center space-y-2">
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
              >
                {t('reset_password.request_new_link')}
              </Link>
              <div className="text-sm text-gray-600">
                {t('reset_password.remember_password')}{' '}
                <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-500">
                  {t('reset_password.signin_link')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
